'use client';

import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import Divider from '../Divider';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  className?: string;
  children: React.ReactNode;
  animate?: boolean;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  className = '',
  children,
  animate = true,
}) => {
  // For exit animation delay (only needed when animating)
  const [shouldRender, setShouldRender] = useState(isOpen);

  // Handle open and close delay when animations are enabled
  useEffect(() => {
    let timeout: NodeJS.Timeout | undefined;

    if (isOpen) {
      setShouldRender(true);
    } else if (shouldRender && animate) {
      // Only delay if animation is wanted
      timeout = setTimeout(() => {
        setShouldRender(false);
      }, 300); // animation duration
    } else if (!animate) {
      // No animation unmount immediately when closed
      setShouldRender(false);
    }

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [isOpen, shouldRender, animate]);

  // MUI-style scroll lock: hide overflow and offset scrollbar width so layout doesn't shift
  useEffect(() => {
    if (!isOpen) return;

    const { body } = document;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    const previousOverflow = body.style.overflow;
    const previousPaddingRight = body.style.paddingRight;
    const computedPaddingRight = Number.parseInt(
      window.getComputedStyle(body).paddingRight,
      10,
    ) || 0;

    body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      body.style.paddingRight = `${computedPaddingRight + scrollbarWidth}px`;
    }

    return () => {
      body.style.overflow = previousOverflow;
      body.style.paddingRight = previousPaddingRight;
    };
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!shouldRender) return null;

  let animationClass = '';
  if (animate) {
    animationClass = isOpen ? 'animate-slide-down' : 'animate-slide-up';
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close modal"
        onClick={onClose}
        className={`
          absolute inset-0 bg-black/50
          transition-opacity duration-300
          ${isOpen ? 'opacity-100' : 'opacity-0'}
        `}
      />

      {/* Modal content */}
      <div
        className={`
          relative z-10 rounded-2xl p-4 bg-white shadow-2xl max-w-2xl mx-4
          ${animationClass}
          ${className}
        `}
      >
        {/* Header */}
        <div className="flex flex-col px-8 py-4">
          <div className="flex items-center justify-between">
            {title ? (
              <h2 className="text-lg font-semibold">{title}</h2>
            ) : (
              <span />
            )
            }

            <button
              onClick={onClose}
              className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
              aria-label="Close modal"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          {title && <Divider />}
        </div>


        {/* Content */}
        <div className="px-8 pb-8">{children}</div>
      </div>
    </div>
  );
};

export default Modal;